import { useConfig, useNextgenRouter, useSession, useTranslation } from '@nextgen-web-framework/all';
import { Logger } from '@nextgen-web-framework/core';
import {
  AccountProcessType,
  RegFlowConfig,
  RoutePaths,
  UsernameContactType,
} from '@commerce-profile-webcomponents/sdk';
import RegistrationConfirmHeaderComponent from '../Common/RegistrationConfirmHeaderComponent/RegistrationConfirmHeaderComponent';
import AccountOrderDetails from '../AccountOrderDetails/AccountOrderDetails';
import { IRegistrationSuccessProps } from './types/Proptypes';
import styles from './RegistrationSuccessComponent.module.scss';

const RegistrationSuccessComponent = ({
  accountId,
  sponsorAccountId,
  sponsorName,
  message,
  imageUrl,
  prospectInfo,
  flowName,
  email,
  phonenumber,
  primaryContactType,
}: IRegistrationSuccessProps) => {
  const router = useNextgenRouter();
  const logger = Logger.getInstance();
  const t = useTranslation();
  const { isAnonymous } = useSession();
  const isAboLedUplineFlow = flowName === RegFlowConfig.AboLedFlow && !isAnonymous();
  const amwayId = primaryContactType === UsernameContactType.Email ? email : phonenumber;
  const configs = useConfig();

  const primaryButtonHandler = () => {
    logger.log('redirect to home');
    const redirectUrl = configs.NEXT_PUBLIC_REGISTER_REDIRECT_URL ?? '/complete-your-profile';
    isAboLedUplineFlow
      ? router.push(`/signup?flowName=${RegFlowConfig.AboLedFlow}&returnUri=${redirectUrl}`)
      : router.push(RoutePaths.HOME);
  };

  const secondaryButtonHandler = () => {
    logger.log('redirect');
    router.push(RoutePaths.HOME);
  };

  return (
    <div className={styles['reg-success-container']}>
      <RegistrationConfirmHeaderComponent
        heading={t('profile.RegistrationConfirmation.congratulations')}
        message={message ? t(message) : ''}
        aboInfoNote={
          isAboLedUplineFlow
            ? t('profile.RegistrationConfirmHeaderComponent.aboInfoNote', {
                username:
                  (prospectInfo?.primaryContactType === UsernameContactType.Phone
                    ? prospectInfo?.phonenumber
                    : prospectInfo?.email) ?? '',
              })
            : ''
        }
        primaryButtonLabel={
          isAboLedUplineFlow
            ? 'profile.RegistrationConfirmation.registerMore'
            : 'profile.RegistrationConfirmation.goShopping'
        }
        primaryButtonCallback={primaryButtonHandler}
        secondaryButtonLabel={
          isAboLedUplineFlow
            ? 'profile.RegistrationConfirmation.goShopping'
            : 'profile.RegistrationConfirmation.viewDetails'
        }
        secondaryButtonCallback={secondaryButtonHandler}
        imageUrl={imageUrl}
        amwayId={isAboLedUplineFlow ? '' : amwayId}
      />
      <div className={styles['reg-success-account']}>
        <div className={styles['reg-success-account-detail']}>
          {isAboLedUplineFlow ? (
            <>
              <div className={styles['reg-success-prospect-details']}>
                <div className={styles['reg-success-prospect-title']}>
                  {t('profile.RegistrationSuccessComponent.prospectTitle')}
                </div>
                <div className={styles['reg-success-prospect-info']}>
                  <span className={styles['reg-success-prospect-label']}>
                    {t('profile.RegistrationSuccessComponent.prospectFirstName')}
                  </span>
                  <span className={styles['reg-success-prospect-data']}>{prospectInfo?.firstName}</span>
                </div>
                <div className={styles['reg-success-prospect-info']}>
                  <span className={styles['reg-success-prospect-label']}>
                    {t('profile.RegistrationSuccessComponent.prospectLastName')}
                  </span>
                  <span className={styles['reg-success-prospect-data']}>{prospectInfo?.lastName}</span>
                </div>
                <div className={styles['reg-success-prospect-info']}>
                  <span className={styles['reg-success-prospect-label']}>
                    {t('profile.RegistrationSuccessComponent.prospectEmail')}
                  </span>
                  <span className={styles['reg-success-prospect-data']}>{prospectInfo?.email}</span>
                </div>
                {prospectInfo?.phonenumber && (
                  <div className={styles['reg-success-prospect-info']}>
                    <span className={styles['reg-success-prospect-label']}>
                      {t('profile.RegistrationSuccessComponent.prospectMobile')}
                    </span>
                    <span className={styles['reg-success-prospect-data']}>{prospectInfo?.phonenumber}</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <AccountOrderDetails
              accountNumber={accountId?.toString() ?? ''}
              sponsor={{
                accountNumber: sponsorAccountId?.toString() ?? '',
                accountName: sponsorName ?? '',
              }}
              AccountProcessType={AccountProcessType.Registration}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export { RegistrationSuccessComponent };
